import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import vhCheck from 'vh-check';

enum LanguageTypes {
  Spanish = 'es',
  English = 'en',
  Portuguese = 'pt',
}

interface UIContextType {
  windowWidth: number;
  windowHeight: number;
  isMobile: boolean;
  isMediumScreen: boolean;
  openSider: boolean;
  setOpenSider: (open: boolean) => void;
  localeLanguage: string;
  isSafari: boolean;
  userLanguageDecimalSeparator: string;
}

const UIContext = createContext<UIContextType>({} as UIContextType);

export const UIProvider = ({ children }: { children: ReactNode }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [localeLanguage, setLocaleLanguage] = useState('');
  const [openSider, setOpenSider] = useState(true);
  const isMobile = windowWidth < 600;
  const isMediumScreen = windowWidth < 992;
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();

    setIsSafari(userAgent.includes('safari') && !userAgent.includes('chrome'));
  }, []);

  useEffect(() => {
    const lang = getLocaleLanguage();

    setLocaleLanguage(lang);

    // Listener
    const resizeListener = (resizeEvent: any) => {
      const { offset, windowHeight } = vhCheck();

      document.documentElement.style.setProperty('--vh-offset', `${offset}px`);

      setWindowHeight(windowHeight);
      setWindowWidth(resizeEvent.currentTarget.innerWidth);
    };

    if (typeof window === 'object') {
      // Metodo que se ejecuta al resize de la ventana.
      window.addEventListener('resize', resizeListener, false);
    }

    return () => {
      if (typeof window === 'object') {
        window.removeEventListener('resize', resizeListener);
      }
    };
  }, []);

  useEffect(() => {
    if (isMediumScreen) {
      setOpenSider(false);
    } else {
      setOpenSider(true);
    }
  }, [isMediumScreen]);

  const userLanguageDecimalSeparator = useMemo(() => {
    if (!localeLanguage) {
      return '.';
    }

    const numeroEjemplo = 1.1; // Formatear el número de ejemplo según la configuración regional
    const numeroFormateado = numeroEjemplo.toLocaleString(localeLanguage); // Obtener el separador decimal del número formateado
    const separadorDecimal = numeroFormateado.replace(/\d/g, '');

    return separadorDecimal;
  }, [localeLanguage]);

  return (
    <UIContext.Provider
      value={{
        windowWidth,
        windowHeight,
        isMobile,
        isMediumScreen,
        openSider,
        setOpenSider: (open: boolean) => setOpenSider(open),
        localeLanguage,
        isSafari,
        userLanguageDecimalSeparator,
      }}>
      {children}
    </UIContext.Provider>
  );
};

export const useUiContext = () => useContext(UIContext);

const getLocaleLanguage = () => {
  const lng = (window.navigator.language || '').split('-')[0];

  return [LanguageTypes.English, LanguageTypes.Spanish, LanguageTypes.Portuguese].includes(
    lng as LanguageTypes,
  )
    ? lng
    : LanguageTypes.Spanish;
};
